import {
  ContentCopy,
  DownloadRounded,
  Person,
  ThumbDownAlt,
  ThumbDownOffAlt,
  ThumbUpAlt,
  ThumbUpOffAlt,
  VolumeUpRounded,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Skeleton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  createRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Markdown from "react-markdown";
import { IConversation } from "../types/chatbot";
import Graphs from "./graphs";
import StarIcon from "./star_icon";
import { Link, useSearchParams } from "react-router-dom";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import rehypeRaw from "rehype-raw";
import DebugModal from "./debug_modal";
import { useAuth } from "../provider/auth_provider";
import LegalFeedback from "./LegalFeedback";
import { toast } from "react-toastify";
import logo from "../assets/HDFC-Ergo-logo.png";

export default function Conversation({
  conversation,
  like,
  dislike,
  tool,
  dislikeReason,
}: {
  conversation: IConversation;
  like: () => void;
  dislike: (feedbackReason?: any) => void;
  tool: string;
  dislikeReason?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [clickCount, setClickCount] = useState(0);
  const [dislikeOpen, setIsDislikeOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const { source } = useAuth();
  // const [searchParams] = useSearchParams();
  // const docChatType = searchParams.get("type") || "document";

  const urlArray = useMemo(() => {
    return conversation?.url?.split(",").map((url) => url.trim()) || null;
  }, [conversation?.url]);

  function speak(message: string) {
    if (window.speechSynthesis.speaking) {
      window.speechSynthesis.cancel();
    } else {
      var speech = new SpeechSynthesisUtterance();
      speech.text = message;
      window.speechSynthesis.speak(speech);
    }
  }

  const handleClose = useCallback(() => {
    setTimeout(() => {
      setIsDislikeOpen(false);
    }, 100);
  }, []);

  const dislikeHandler = useCallback(
    (reason: any): any => {
      if (dislikeReason && !reason) {
        setIsDislikeOpen(true);
      } else if (reason && dislikeReason) {
        dislike(reason);
        setTimeout(() => {
          setIsDislikeOpen(false);
        }, 100);
      } else {
        dislike();
      }
    },
    [dislikeReason],
  );

  useEffect(() => {
    if (clickCount > 0) {
      const timeout = setTimeout(() => {
        setClickCount(0);
      }, 10000);

      // Clear timeout if user clicks again before the timeout
      return () => clearTimeout(timeout);
    }
  }, [clickCount]);

  const handleModal = () => {
    setOpen(!open);
  };

  const handleClick = () => {
    if (tool === "AMA @ Company" && !conversation.isLoading) {
      setClickCount((prevCount) => {
        const newCount = prevCount + 1;
        if (newCount >= 5) {
          handleModal();
          return 0;
        }

        return newCount;
      });
    }
  };

  function download() {
    if (!ref.current) return;

    const element = ref.current;

    const header = document.createElement("div");
    if (
      ["Chat @ WC Claims", "Chat @ Tech E&O", "Chat @ Tech D&O"].includes(tool)
    ) {
      // CREATE PDF HEADER
      header.id = "pdf-header";
      header.style.display = "flex";
      header.style.alignItems = "center";
      header.style.gap = "16px";
      header.style.marginBottom = "20px";
      header.style.padding = "10px 0";

      header.innerHTML = `
    <img 
      alt="HDFC Ergo Logo"
      style="height:60px; object-fit:contain;"
    />
    <div>
      <h2 style="margin:0; font-size:20px;">HDFC ERGO General Insurance Company Limited</h2>
      <!-- <p style="margin:4px 0 0; font-size:12px; color:#555;">
        Conversation Report
      </p> -->
    </div>
  `;

      (header.querySelector("img") as HTMLImageElement).src = logo;

      // PREPEND HEADER with Logo
      element.prepend(header);
    }

    // Find the inner content container that has maxWidth restriction
    const contentContainer = element.querySelector(
      ".markdown-container",
    ) as HTMLElement;

    const originalMaxWidth = contentContainer?.style.maxWidth;
    const originalOverflow = contentContainer?.style.overflow;

    // Remove restrictions
    if (contentContainer) {
      contentContainer.style.maxWidth = "none";
      contentContainer.style.overflow = "visible";
    }

    const filename = [
      "Chat @ WC Claims",
      "Chat @ Tech E&O",
      "Chat @ Tech D&O",
    ].includes(tool)
      ? `${conversation?.inputType}_${Date.now()}.pdf`
      : "conversation.pdf";

    (window as any)
      .html2pdf()
      .set({
        margin: 5,
        filename,
        html2canvas: {
          scale: 2,
          useCORS: true,
          scrollX: 0,
          scrollY: 0,
          windowWidth: element.scrollWidth, // VERY IMPORTANT
        },
        jsPDF: {
          unit: "mm",
          format: "a3",
          orientation: "landscape",
        },
      })
      .from(element)
      .save()
      .then(() => {
        // Reset Header
        header.remove();

        // Restore styles
        if (contentContainer) {
          contentContainer.style.maxWidth = originalMaxWidth || "";
          contentContainer.style.overflow = originalOverflow || "";
        }
      });
  }

  const copy = async () => {
    if (!ref.current) return;

    const element = ref.current;

    // 1. Create a "hidden" clone of the element to manipulate it
    const clone = element.cloneNode(true) as HTMLElement;

    // 2. Remove UI elements (like buttons)
    const UIButtons = clone.querySelectorAll("button, .no-copy");
    UIButtons.forEach((btn) => btn.remove());

    // 3. Inject Inline Styles for Borders
    const tables = clone.querySelectorAll("table");
    const cells = clone.querySelectorAll("th, td");

    tables.forEach((table) => {
      (table as HTMLElement).style.borderCollapse = "collapse";
      (table as HTMLElement).style.width = "100%";
      (table as HTMLElement).style.margin = "10px 0";
    });

    cells.forEach((cell) => {
      (cell as HTMLElement).style.border = "1px solid #cccccc";
      (cell as HTMLElement).style.padding = "8px";
    });

    try {
      // 4. Create the Blobs from our Modified Clone
      const htmlContent = clone.outerHTML;
      const plainText = clone.innerText;

      const blobHtml = new Blob([htmlContent], { type: "text/html" });
      const blobText = new Blob([plainText], { type: "text/plain" });

      const data = [
        new ClipboardItem({
          "text/html": blobHtml,
          "text/plain": blobText,
        }),
      ];

      await navigator.clipboard.write(data);
      toast.success("Details Copied!");
    } catch (err) {
      console.error("Copy failed:", err);
      toast.error(`Copy failed: ${err}`);
    }
  };

  useEffect(() => {
    if (ref.current) {
      const el = ref.current;
      requestAnimationFrame(() => {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  }, [conversation]);
  return (
    <Box mb={isSmallScreen ? 2 : 4} ref={ref} p={isSmallScreen ? 1 : 0}>
      <Box pr={isSmallScreen ? 0 : 2} marginBottom={0.5}>
        <List disablePadding>
          <ListItem sx={{ paddingLeft: 2 }}>
            <ListItemAvatar sx={{ minWidth: 48 }}>
              <Avatar sx={{ width: 32, height: 32 }}>
                <Person />
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={conversation.query}
              primaryTypographyProps={
                isSmallScreen
                  ? { fontSize: 13, fontWeight: 500 }
                  : { fontSize: 15, fontWeight: 500 }
              }
            ></ListItemText>
          </ListItem>
        </List>
      </Box>

      <Box
        ml={isSmallScreen ? 1 : 0.5}
        pt={isSmallScreen ? 0 : 1}
        width={"100%"}
        // maxWidth={isSmallScreen ? "auto" : "70vw"}
        display={"flex"}
        alignItems={"flex-start"}
        borderRadius={2}
        bgcolor={"#fff"}
        py={isSmallScreen ? 1.6 : 2}
        px={isSmallScreen ? 1 : 2}
        pl={isSmallScreen ? 0 : 1.4}
      >
        <Box>
          <Button onClick={handleClick}>
            <StarIcon isLoading={conversation.isLoading} />
          </Button>
        </Box>
        <Box
          flex={1}
          pr={isSmallScreen ? 0 : 1}
          pl={isSmallScreen ? 0 : 1}
          sx={{ maxWidth: "90%" }}
        >
          {conversation.isLoading ? (
            <Box pl={1} sx={{ marginTop: "-0.125rem" }}>
              <Skeleton height={32} width={"100%"} />
            </Box>
          ) : (
            <Box
              sx={
                isSmallScreen
                  ? { fontSize: 14, padding: 0 }
                  : { maxWidth: "100%", overflow: "auto", fontSize: 15 }
              }
              className="markdown-container"
              // dangerouslySetInnerHTML={{
              //   __html: (conversation.result || "").replace(/\n/ig,"<br />"),
              // }}
            >
              <Markdown
                remarkPlugins={[remarkGfm, remarkBreaks]}
                rehypePlugins={[rehypeRaw]}
                components={{
                  a: ({ node, ...props }) => (
                    <a {...props} target="_blank" rel="noopener noreferrer">
                      {props.children}
                    </a>
                  ),
                }}
              >
                {conversation.result ||
                  "Sorry! Can you please rephrase your query?"}
              </Markdown>

              {urlArray &&
                urlArray.map(
                  (url, index) =>
                    url && (
                      <Typography sx={{ mt: 1, fontSize: 13 }} key={index}>
                        <Link to={url} target="_blank">
                          {url}
                        </Link>
                      </Typography>
                    ),
                )}

              {!conversation.isLoading &&
                conversation.comparison &&
                conversation.comparison.length > 0 && (
                  <Graphs type="compare table" data={conversation.comparison} />
                )}

              {!conversation.isLoading && conversation.isPlottable && (
                <Graphs
                  type={conversation.displyType}
                  data={conversation.message}
                />
              )}
            </Box>
          )}

          {!conversation.isLoading && (
            <Box pt={2} display={"flex"} justifyItems={"flex-start"}>
              <IconButton
                onClick={() => speak(conversation.result!)}
                sx={{ width: 32, height: 32 }}
              >
                <VolumeUpRounded sx={{ fontSize: 18 }} />
              </IconButton>
              {!source && (
                <>
                  <IconButton onClick={like} sx={{ width: 32, height: 32 }}>
                    {conversation.isLiked === true ? (
                      <ThumbUpAlt sx={{ fontSize: 18 }} />
                    ) : (
                      <ThumbUpOffAlt sx={{ fontSize: 18 }} />
                    )}
                  </IconButton>
                  <IconButton
                    onClick={() => dislikeHandler("")}
                    sx={{ width: 32, height: 32 }}
                  >
                    {dislikeReason && (
                      <LegalFeedback
                        dislikeOpen={dislikeOpen}
                        dislikeHandler={dislikeHandler}
                        handleClose={handleClose}
                      />
                    )}
                    {conversation.isLiked === false ? (
                      <ThumbDownAlt sx={{ fontSize: 18 }} />
                    ) : (
                      <ThumbDownOffAlt sx={{ fontSize: 18 }} />
                    )}
                  </IconButton>
                </>
              )}
              <IconButton onClick={download} sx={{ width: 32, height: 32 }}>
                <DownloadRounded sx={{ fontSize: 18 }} />
              </IconButton>
              {[
                "Chat @ WC Claims",
                "Chat @ Tech E&O",
                "Chat @ Tech D&O",
                "Chat @ DTR",
              ].includes(tool) && (
                <IconButton onClick={copy} sx={{ width: 32, height: 32 }}>
                  <ContentCopy sx={{ fontSize: 18 }} />
                </IconButton>
              )}
            </Box>
          )}
        </Box>
      </Box>
      <DebugModal
        open={open}
        handleModal={handleModal}
        conversation={conversation}
      />
    </Box>
  );
}
